import React, { useEffect, useRef } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { Form, Input, InputNumber, Modal, Select, Typography } from 'antd';
// Types
import { TicketTierFields } from 'api/ticketing/types/TicketTierFields';
import { TicketCompCategory } from 'api/graphql-global-types';
// Hooks
import { IssueCompsFormValues, useIssueComps } from './hooks/useIssueComps';
// Helpers
import { COMP_CATEGORY_OPTIONS, validateForm } from 'pages/Events/helpers';
// Components
import UserSelect from '../UserSelect/UserSelect';

const { Paragraph } = Typography;

export const MAX_COMP_QUANTITY = 100;

type IssueCompsModalProps = {
  ticketEventId: string;
  tiers: TicketTierFields[];
  visible: boolean;
  onClose: () => void;
  onIssued: () => void;
};

const IssueCompsModal = ({
  ticketEventId,
  tiers,
  visible,
  onClose,
  onIssued,
}: IssueCompsModalProps): JSX.Element => {
  const [form] = Form.useForm<IssueCompsFormValues>();
  // One key per dialog: a retry after a timeout must not issue a second set
  const idempotencyKey = useRef(uuidv4());

  useEffect(() => {
    if (visible) {
      idempotencyKey.current = uuidv4();
    }
  }, [visible]);
  const { issueComps, issuing } = useIssueComps(ticketEventId);

  const handleOk = async (): Promise<void> => {
    const values = await validateForm(form);

    if (!values) {
      return;
    }

    const issued = await issueComps(values, idempotencyKey.current);

    if (issued) {
      form.resetFields();
      onIssued();
    }
  };

  return (
    <Modal
      title="Issue comp tickets"
      visible={visible}
      onCancel={onClose}
      onOk={handleOk}
      okText="Issue comps"
      confirmLoading={issuing}
      destroyOnClose
    >
      <Paragraph type="secondary">
        Free tickets come out of the tier&apos;s inventory. The guest gets no
        confirmation email; the tickets show up in their account.
      </Paragraph>
      <Form<IssueCompsFormValues>
        form={form}
        layout="vertical"
        preserve={false}
        initialValues={{
          quantity: 1,
          category: TicketCompCategory.Guest,
          tierId: tiers.length === 1 ? tiers[0].id : undefined,
        }}
      >
        <Form.Item
          name="tierId"
          label="Tier"
          rules={[{ required: true, message: 'Choose a tier' }]}
        >
          <Select
            placeholder="Choose a tier"
            options={tiers.map((tier) => ({
              value: tier.id,
              label: tier.label,
            }))}
          />
        </Form.Item>
        <Form.Item
          name="quantity"
          label="Tickets"
          rules={[
            { required: true, message: 'How many tickets?' },
            {
              type: 'integer',
              min: 1,
              max: MAX_COMP_QUANTITY,
              message: `From 1 to ${MAX_COMP_QUANTITY}`,
            },
          ]}
        >
          <InputNumber min={1} max={MAX_COMP_QUANTITY} precision={0} />
        </Form.Item>
        <Form.Item
          name="category"
          label="Category"
          rules={[{ required: true, message: 'Choose a category' }]}
        >
          <Select options={COMP_CATEGORY_OPTIONS} />
        </Form.Item>
        <Form.Item
          name="recipientUserId"
          label="Recipient"
          extra="The account the tickets go to. Leave empty to keep them on your own account and hand them out."
        >
          <UserSelect allowClear aria-label="Recipient" />
        </Form.Item>
        <Form.Item
          name="note"
          label="Note"
          rules={[{ max: 1000, message: 'At most 1,000 characters' }]}
        >
          <Input.TextArea rows={2} placeholder="Who it's for and why" />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default IssueCompsModal;
